import React, { useEffect } from "react";
import { Container } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useProfile } from "../../../../Components/Hooks/UserHooks";
import { getReportProject as onGetReportProject, getPhases } from "../../../../slices/thunks";
import { fetchBlockReport } from "../../../../slices/report/thunk";
import { clearBlockReport } from "../../../../slices/report/reducer";
import {
  selectBlockReport, selectBlockReportLoading,
  selectBlockMetadata, selectBlockRevenueKpis, selectBlockRevenueFlow,
  selectBlockCostBreakdown, selectBlockCostKpis, selectUnitBreakdown,
} from "../../../../slices/report/selectors";
import { selectPhases } from "../../../../slices/phases/selectors";

import RevenueKpiCards from "../components/RevenueKpiCards";
import RevenueFlowChart from "../components/RevenueFlowChart";
import CostKpiCards from "../components/CostKpiCards";
import CostHierarchyTable from "../components/CostHierarchyTable";
import VendorList from "../components/VendorList";
import UnitTypeBreakdown from "../components/UnitTypeBreakdown";
import { SkeletonBlockView } from "../components/ReportingSkeleton";
const MetaChip = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: "5px 14px", border: "1px solid rgba(255,255,255,0.12)" }}>
    <i className={icon} style={{ fontSize: "0.85rem", color: "#adc7f7" }} />
    <div>
      <div style={{ fontSize: "0.6rem", color: "#adc7f7", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontSize: "0.82rem", color: "white", fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    </div>
  </div>
);

const RevenueBlockView = () => {
  const { projectId, phaseId, blockId } = useParams();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { userProfile } = useProfile();

  const phases       = useSelector(selectPhases);
  const loading      = useSelector(selectBlockReportLoading);
  const blockMeta    = useSelector(selectBlockMetadata);
  const revenueKpis  = useSelector(selectBlockRevenueKpis);
  const revenueFlow  = useSelector(selectBlockRevenueFlow);
  const costBreakdown = useSelector(selectBlockCostBreakdown);
  const costKpis     = useSelector(selectBlockCostKpis);
  const unitBreakdown = useSelector(selectUnitBreakdown);

  const currentPhase = phases.find((p) => String(p.id) === phaseId) || null;
  const currentBlock = currentPhase?.blocks?.find((b) => String(b.id) === blockId) || blockMeta || null;
  const blockName    = currentBlock?.name || blockMeta?.name || "Block";

  useEffect(() => {
    if (!userProfile) return;
    dispatch(onGetReportProject(projectId));
    dispatch(getPhases());
    dispatch(fetchBlockReport({ projectId, phaseId, blockId }));
    return () => { dispatch(clearBlockReport()); };
  }, [dispatch, projectId, phaseId, blockId, userProfile]);

  const tableGroups = costBreakdown.map((ct) => ({
    id: ct.id, name: ct.name,
    budget: ct.budget ?? 0, spent: ct.spent ?? 0, committed: ct.committed ?? 0,
    categories: (ct.categories || []).map((cat) => ({
      name: cat.name, budget: cat.budget ?? 0, spent: cat.spent ?? 0, committed: cat.committed ?? 0,
    })),
  }));

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <SkeletonBlockView />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>

        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          <button onClick={() => navigate(`/project-reporting/${projectId}/revenue`)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 0, fontSize: "inherit", fontWeight: "inherit", letterSpacing: "inherit", textTransform: "inherit" }}>
            Project
          </button>
          <i className="ri-arrow-right-s-line" style={{ color: "#cbd5e1" }} />
          <button onClick={() => navigate(`/project-reporting/${projectId}/revenue?phase=${phaseId}`)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 0, fontSize: "inherit", fontWeight: "inherit", letterSpacing: "inherit", textTransform: "inherit" }}>
            {currentPhase?.name || "Phase"}
          </button>
          <i className="ri-arrow-right-s-line" style={{ color: "#cbd5e1" }} />
          <span style={{ color: "#002045" }}>{blockName}</span>
        </div>

        {/* Hero header */}
        <div style={{ background: "linear-gradient(135deg, #002045 0%, #1a365d 100%)", borderRadius: 16, padding: "24px 28px", marginBottom: 28, position: "relative", overflow: "hidden" }}>
          <i className="ri-building-4-fill" style={{ position: "absolute", right: -20, bottom: -20, fontSize: 180, color: "white", opacity: 0.04 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className="ri-building-2-line" style={{ fontSize: "1.4rem", color: "white" }} />
            </div>
            <div>
              <h4 className="mb-0 fw-bold text-white" style={{ lineHeight: 1.2 }}>{blockName}</h4>
              <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#adc7f7" }}>{currentPhase?.name || "Phase"}</span>
            </div>
          </div>
          {blockMeta && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <MetaChip icon="ri-home-4-line"          label="Total Flats"  value={blockMeta.total_flats} />
              <MetaChip icon="ri-stack-line"            label="Floors"      value={blockMeta.total_floors} />
              <MetaChip icon="ri-layout-grid-line"      label="Apt Surface" value={`${blockMeta.total_apartment_surface?.toLocaleString()} m²`} />
              <MetaChip icon="ri-sunny-line"            label="Terrace"     value={`${blockMeta.total_terrace_surface?.toLocaleString()} m²`} />
            </div>
          )}
          {loading && !blockMeta && (
            <div style={{ display: "flex", gap: 8 }}>
              {[100, 80, 110, 90].map((w, i) => (
                <div key={i} style={{ height: 44, width: w, background: "rgba(255,255,255,0.08)", borderRadius: 20 }} />
              ))}
            </div>
          )}
        </div>

        {/* Revenue KPIs */}
        {revenueKpis && (
          <RevenueKpiCards
            totalListing={revenueKpis.total_listing ?? 0}
            totalSold={revenueKpis.total_sold ?? 0}
            totalListingSold={revenueKpis.total_listing_sold ?? 0}
            totalCollected={revenueKpis.total_collected ?? 0}
            remainingToSell={revenueKpis.remaining_to_sell ?? 0}
            soldUnits={revenueKpis.sold_units ?? 0}
            totalUnits={revenueKpis.total_units ?? 0}
          />
        )}

        {/* Unit breakdown + Revenue flow */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 28 }}>
          <UnitTypeBreakdown data={unitBreakdown} />
          {revenueFlow && <RevenueFlowChart flow={revenueFlow} />}
        </div>

        {/* Cost KPIs */}
        {costKpis && (
          <CostKpiCards
            totalBudget={costKpis.total_budget}
            totalSpent={costKpis.total_spent}
            committed={costKpis.total_committed}
            remaining={(costKpis.total_budget ?? 0) - (costKpis.total_spent ?? 0) - (costKpis.total_committed ?? 0)}
            unexpectedBudget={costKpis.unexpected_budget}
            unexpectedSpent={costKpis.unexpected_spent}
            unexpectedRemaining={costKpis.unexpected_remaining}
            unexpectedAbsorbed={costKpis.unexpected_overrun_absorbed}
          />
        )}

        {/* Vendors + Cost hierarchy */}
        <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 24, alignItems: "stretch", paddingBottom: 32 }}>
          <VendorList phaseId={phaseId} blockId={blockId} />
          <CostHierarchyTable groups={tableGroups.length > 0 ? tableGroups : undefined} />
        </div>

      </Container>
    </div>
  );
};

export default RevenueBlockView;
